/**
 * 비밀번호 정책 (클라이언트 미러) — PC + Mobile 공용
 *
 * 정책: 8자 이상 + 소문자 포함 + 특수문자 포함
 *
 * ⚠ 서버 원본: server/src/utils/passwordPolicy.js
 *   빌드가 분리되어 있어 import 가 불가능하므로 규칙을 미러링한다.
 *   한쪽을 수정하면 반드시 다른 쪽도 동일하게 수정할 것 (lib/userCapability.js 와 같은 패턴).
 *
 * ⚠ 이 검증은 사용자 안내(UX)용이다. 최종 판정은 항상 서버가 한다.
 *   평문 비밀번호를 로깅/저장/전송(변경 API 외)하지 않는다.
 */

export const PASSWORD_POLICY = Object.freeze({
  minLength: 8,
  requireLowercase: true,
  requireSpecial: true
});

/** 사용자 안내 문구 (플레이스홀더/도움말에서 재사용) */
export const PASSWORD_POLICY_TEXT = '8자 이상, 소문자와 특수문자 포함';

/** 조건별 안내 문구 (실패 시에만 사용) */
export const RULE_MESSAGES = Object.freeze({
  length: `비밀번호는 ${PASSWORD_POLICY.minLength}자 이상이어야 합니다`,
  lowercase: '비밀번호에 소문자를 포함해야 합니다',
  special: '비밀번호에 특수문자를 포함해야 합니다'
});

/** 체크리스트 UI 표시용 (서버 checks 키와 동일 순서/키) */
export const PASSWORD_RULES = Object.freeze([
  { key: 'length', label: `${PASSWORD_POLICY.minLength}자 이상` },
  { key: 'lowercase', label: '소문자 포함' },
  { key: 'special', label: '특수문자 포함' }
]);

const LOWERCASE_RE = /[a-z]/;

/**
 * 특수문자 = ASCII 구두점 (코드포인트 33~47, 58~64, 91~96, 123~126).
 * 한글/공백은 특수문자로 인정하지 않는다 (서버와 동일 규칙).
 */
function isSpecialChar(ch) {
  const c = ch.charCodeAt(0);
  return (c >= 33 && c <= 47) || (c >= 58 && c <= 64) || (c >= 91 && c <= 96) || (c >= 123 && c <= 126);
}

function hasSpecialChar(value) {
  for (const ch of value) {
    if (isSpecialChar(ch)) return true;
  }
  return false;
}

/**
 * 비밀번호 정책 판정 (순수 함수 — 서버 checkPassword 와 동일 결과)
 * @param {string} password
 * @returns {{ valid: boolean, checks: { length: boolean, lowercase: boolean, special: boolean }, errors: string[] }}
 */
export function checkPassword(password) {
  const value = typeof password === 'string' ? password : '';

  const checks = {
    length: value.length >= PASSWORD_POLICY.minLength,
    lowercase: LOWERCASE_RE.test(value),
    special: hasSpecialChar(value)
  };

  const errors = [];
  if (!checks.length) errors.push(RULE_MESSAGES.length);
  if (!checks.lowercase) errors.push(RULE_MESSAGES.lowercase);
  if (!checks.special) errors.push(RULE_MESSAGES.special);

  return { valid: errors.length === 0, checks, errors };
}

/** boolean 축약형 */
export function isValidPassword(password) {
  return checkPassword(password).valid;
}

/** 실패 사유를 한 줄 안내 문자열로. 통과하면 null. */
export function describePasswordErrors(password) {
  const { valid, errors } = checkPassword(password);
  return valid ? null : errors.join(', ');
}
